/**
 * ActiveBoardContext — React context for managing the currently viewed board
 * with lists, swimlanes, cards, and optimistic updates for create/move/update.
 *
 * Requirements: 5.1, 5.2, 5.3, 5.5
 */

import React, { createContext, useCallback, useContext, useEffect, useReducer, useRef } from 'react';
import * as api from '../services/api-client';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface BoardDetail {
  readonly boardId: string;
  readonly title: string;
  readonly visibility: 'private' | 'public-read' | 'public-edit';
  readonly description: string;
  readonly color: string;
  readonly archived: boolean;
  readonly createdAt: string;
  readonly members: readonly BoardMember[];
  readonly labels: readonly BoardLabel[];
}

export interface BoardMember {
  readonly userId: string;
  readonly displayName: string;
  readonly role: string;
}

export interface BoardLabel {
  readonly labelId: string;
  readonly name: string;
  readonly color: string;
}

export interface ListSummary {
  readonly listId: string;
  readonly title: string;
  readonly sort: number;
  readonly archived: boolean;
  readonly color: string;
  readonly width: number;
  readonly wipLimit: number | null;
  readonly wipMode: 'soft' | 'hard' | null;
  readonly wipEnabled: boolean;
  readonly cardCount: number;
  readonly isDone: boolean;
}

export interface SwimlaneSummary {
  readonly swimlaneId: string;
  readonly title: string;
  readonly sort: number;
  readonly archived: boolean;
  readonly color: string;
  readonly cardCount: number;
  readonly startDate: string | null;
  readonly endDate: string | null;
}

export interface CardSummary {
  readonly cardId: string;
  readonly cardNumber: number;
  readonly title: string;
  readonly listId: string;
  readonly swimlaneId: string;
  readonly sort: number;
  readonly labels: readonly string[];
  readonly members: readonly { userId: string; displayName: string }[];
  readonly memberAvatars: readonly string[];
  readonly dueDate: string | null;
  readonly startDate: string | null;
  readonly coverImage: string | null;
  readonly checklistProgress: { completed: number; total: number } | null;
  readonly subtaskProgress: { completed: number; total: number } | null;
  readonly isSubtask: boolean;
  readonly parentCardId: string | null;
  readonly customFields: Record<string, boolean | string | string[]>;
  readonly description?: string;
  readonly showDescriptionOnCard?: boolean;
}

export type FieldType = 'boolean' | 'dropdown-single' | 'dropdown-multi' | 'text' | 'number' | 'linked-to-list';

export interface CustomFieldDefinition {
  readonly fieldId: string;
  readonly boardId: string;
  readonly name: string;
  readonly fieldType: FieldType;
  readonly options: readonly string[];
  readonly showOnCard: boolean;
  readonly sort: number;
}

export interface ActiveBoardState {
  readonly board: BoardDetail | null;
  readonly lists: readonly ListSummary[];
  readonly swimlanes: readonly SwimlaneSummary[];
  readonly cards: readonly CardSummary[];
  readonly customFieldDefinitions: readonly CustomFieldDefinition[];
  readonly isLoading: boolean;
  readonly error: string | null;
  readonly accessLevel: 'admin' | 'normal' | 'read-only' | 'none';
  readonly isMember: boolean;
}

// ---------------------------------------------------------------------------
// Action types
// ---------------------------------------------------------------------------

export interface CreateCardInput {
  readonly title: string;
  readonly listId: string;
  readonly swimlaneId: string;
}

export interface MoveCardInput {
  readonly cardId: string;
  readonly targetListId: string;
  readonly targetSwimlaneId: string;
  readonly sort?: number;
}

export interface UpdateCardInput {
  readonly cardId: string;
  readonly title?: string;
  readonly listId?: string;
  readonly swimlaneId?: string;
  readonly dueDate?: string | null;
  readonly startDate?: string | null;
  readonly memberAvatars?: readonly string[];
  readonly members?: readonly { userId: string; displayName: string }[];
  readonly description?: string;
  readonly showDescriptionOnCard?: boolean;
}

export interface RestoreCardInput {
  readonly cardId: string;
  readonly listId: string;
  readonly swimlaneId: string;
}

export interface ReorderListInput {
  readonly listId: string;
  readonly newSort: number;
}

export type ActiveBoardAction =
  | { type: 'FETCH_BOARD_START' }
  | { type: 'FETCH_BOARD_SUCCESS'; payload: { board: BoardDetail; lists: ListSummary[]; swimlanes: SwimlaneSummary[]; cards: CardSummary[]; customFields?: CustomFieldDefinition[]; accessLevel?: string; isMember?: boolean } }
  | { type: 'FETCH_BOARD_ERROR'; payload: string }
  | { type: 'CREATE_CARD_OPTIMISTIC'; payload: CardSummary }
  | { type: 'CREATE_CARD_REVERT'; payload: { cardId: string; previousState: ActiveBoardState } }
  | { type: 'CREATE_CARD_CONFIRMED'; payload: { tempId: string; cardId: string; cardNumber: number } }
  | { type: 'MOVE_CARD_OPTIMISTIC'; payload: MoveCardInput }
  | { type: 'MOVE_CARD_REVERT'; payload: { previousState: ActiveBoardState } }
  | { type: 'MOVE_CARD_CONFIRMED' }
  | { type: 'UPDATE_CARD_OPTIMISTIC'; payload: UpdateCardInput }
  | { type: 'UPDATE_CARD_REVERT'; payload: { previousState: ActiveBoardState } }
  | { type: 'UPDATE_CARD_CONFIRMED' }
  | { type: 'ARCHIVE_CARD_OPTIMISTIC'; payload: { cardId: string } }
  | { type: 'ARCHIVE_CARD_REVERT'; payload: { previousState: ActiveBoardState } }
  | { type: 'ARCHIVE_CARD_CONFIRMED' }
  | { type: 'RESTORE_CARD_OPTIMISTIC'; payload: { card: CardSummary } }
  | { type: 'RESTORE_CARD_REVERT'; payload: { previousState: ActiveBoardState } }
  | { type: 'RESTORE_CARD_CONFIRMED' }
  | { type: 'REORDER_LIST_OPTIMISTIC'; payload: { listId: string; newSort: number } }
  | { type: 'REORDER_LIST_REVERT'; payload: { previousState: ActiveBoardState } }
  | { type: 'REORDER_LIST_CONFIRMED' }
  | { type: 'ARCHIVE_LIST_OPTIMISTIC'; payload: { listId: string } }
  | { type: 'ARCHIVE_LIST_REVERT'; payload: { previousState: ActiveBoardState } }
  | { type: 'ARCHIVE_LIST_CONFIRMED' }
  | { type: 'WS_EVENT'; payload: WebSocketEvent }
  | { type: 'REFETCH_BOARD' };

// ---------------------------------------------------------------------------
// WebSocket event types
// ---------------------------------------------------------------------------

export type WebSocketEvent =
  | { eventType: 'CardCreated'; card: CardSummary }
  | { eventType: 'CardMoved'; cardId: string; listId: string; swimlaneId: string }
  | { eventType: 'CardUpdated'; cardId: string; fields: Partial<CardSummary> }
  | { eventType: 'ListCreated'; list: ListSummary }
  | { eventType: 'ListUpdated'; listId: string; fields: Partial<ListSummary> }
  | { eventType: 'SwimlaneCreated'; swimlane: Partial<SwimlaneSummary> & { swimlaneId: string } }
  | { eventType: 'SwimlaneUpdated'; swimlaneId: string; fields: Partial<SwimlaneSummary> }
  | { eventType: 'MemberAdded'; member: BoardMember }
  | { eventType: 'CustomfieldCreated'; field: CustomFieldDefinition }
  | { eventType: 'CustomfieldUpdated'; field: CustomFieldDefinition }
  | { eventType: 'CustomfieldDeleted'; fieldId: string };

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

export const initialActiveBoardState: ActiveBoardState = {
  board: null,
  lists: [],
  swimlanes: [],
  cards: [],
  customFieldDefinitions: [],
  isLoading: false,
  error: null,
  accessLevel: 'none',
  isMember: false,
};

export function activeBoardReducer(state: ActiveBoardState, action: ActiveBoardAction): ActiveBoardState {
  switch (action.type) {
    case 'FETCH_BOARD_START':
      return { ...state, isLoading: true, error: null };

    case 'FETCH_BOARD_SUCCESS':
      return {
        board: action.payload.board,
        lists: action.payload.lists,
        swimlanes: action.payload.swimlanes,
        cards: action.payload.cards,
        customFieldDefinitions: action.payload.customFields ?? [],
        isLoading: false,
        error: null,
        accessLevel: (action.payload.accessLevel as ActiveBoardState['accessLevel']) ?? 'normal',
        isMember: action.payload.isMember ?? true,
      };

    case 'FETCH_BOARD_ERROR':
      return { ...state, isLoading: false, error: action.payload };

    case 'CREATE_CARD_OPTIMISTIC':
      return { ...state, cards: [...state.cards, action.payload] };

    case 'CREATE_CARD_REVERT':
      return action.payload.previousState;

    case 'CREATE_CARD_CONFIRMED':
      return {
        ...state,
        cards: state.cards.map((c) =>
          c.cardId === action.payload.tempId
            ? { ...c, cardId: action.payload.cardId, cardNumber: action.payload.cardNumber }
            : c,
        ),
      };

    case 'MOVE_CARD_OPTIMISTIC': {
      // Resolve target list title for linked-to-list field auto-sync
      const targetList = state.lists.find((l) => l.listId === action.payload.targetListId);
      const targetListTitle = targetList?.title ?? '';
      const linkedFieldIds = state.customFieldDefinitions
        .filter((d) => d.fieldType === 'linked-to-list')
        .map((d) => d.fieldId);

      return {
        ...state,
        cards: state.cards.map((c) => {
          if (c.cardId !== action.payload.cardId) return c;

          // Build updated custom fields with linked-to-list values
          let updatedCustomFields = c.customFields;
          if (linkedFieldIds.length > 0) {
            updatedCustomFields = { ...c.customFields };
            for (const fieldId of linkedFieldIds) {
              updatedCustomFields[fieldId] = targetListTitle;
            }
          }

          return {
            ...c,
            listId: action.payload.targetListId,
            swimlaneId: action.payload.targetSwimlaneId,
            ...(action.payload.sort !== undefined && action.payload.sort >= 0 ? { sort: action.payload.sort } : {}),
            customFields: updatedCustomFields,
          };
        }),
      };
    }

    case 'MOVE_CARD_REVERT':
      return action.payload.previousState;

    case 'MOVE_CARD_CONFIRMED':
      return state;

    case 'UPDATE_CARD_OPTIMISTIC': {
      const { cardId, ...updates } = action.payload;
      return {
        ...state,
        cards: state.cards.map((c) =>
          c.cardId === cardId
            ? { ...c, ...stripUndefined(updates) }
            : c,
        ),
      };
    }

    case 'UPDATE_CARD_REVERT':
      return action.payload.previousState;

    case 'UPDATE_CARD_CONFIRMED':
      return state;

    case 'ARCHIVE_CARD_OPTIMISTIC':
      return {
        ...state,
        cards: state.cards.filter((c) => c.cardId !== action.payload.cardId),
      };

    case 'ARCHIVE_CARD_REVERT':
      return action.payload.previousState;

    case 'ARCHIVE_CARD_CONFIRMED':
      return state;

    case 'RESTORE_CARD_OPTIMISTIC':
      return { ...state, cards: [...state.cards, action.payload.card] };

    case 'RESTORE_CARD_REVERT':
      return action.payload.previousState;

    case 'RESTORE_CARD_CONFIRMED':
      return state;

    case 'REORDER_LIST_OPTIMISTIC':
      return {
        ...state,
        lists: state.lists.map((l) =>
          l.listId === action.payload.listId
            ? { ...l, sort: action.payload.newSort }
            : l,
        ),
      };

    case 'REORDER_LIST_REVERT':
      return action.payload.previousState;

    case 'REORDER_LIST_CONFIRMED':
      return state;

    case 'ARCHIVE_LIST_OPTIMISTIC':
      return {
        ...state,
        lists: state.lists.map((l) =>
          l.listId === action.payload.listId
            ? { ...l, archived: true }
            : l,
        ),
      };

    case 'ARCHIVE_LIST_REVERT':
      return action.payload.previousState;

    case 'ARCHIVE_LIST_CONFIRMED':
      return state;

    case 'WS_EVENT':
      return mergeWebSocketEvent(state, action.payload);

    case 'REFETCH_BOARD':
      return { ...state, isLoading: true, error: null };

    default:
      return state;
  }
}

// ---------------------------------------------------------------------------
// WebSocket event merge
// ---------------------------------------------------------------------------

/**
 * Normalizes a card arriving from a WebSocket event so the render layer can
 * safely assume its array fields exist. Event payloads (including cards created
 * via the MCP server) don't always include every collection, and a missing
 * `labels`/`members`/`memberAvatars` array crashes the board on `.map`.
 */
function normalizeCard(card: CardSummary): CardSummary {
  return {
    ...card,
    labels: card.labels ?? [],
    members: card.members ?? [],
    memberAvatars: card.memberAvatars ?? [],
  };
}

function mergeWebSocketEvent(state: ActiveBoardState, event: WebSocketEvent): ActiveBoardState {
  switch (event.eventType) {
    case 'CardCreated':
      // Don't duplicate if the card already exists (e.g., from optimistic update)
      if (state.cards.some((c) => c.cardId === event.card.cardId)) {
        return state;
      }
      return { ...state, cards: [...state.cards, normalizeCard(event.card)] };

    case 'CardMoved':
      return {
        ...state,
        cards: state.cards.map((c) =>
          c.cardId === event.cardId
            ? { ...c, listId: event.listId, swimlaneId: event.swimlaneId }
            : c,
        ),
      };

    case 'CardUpdated':
      return {
        ...state,
        cards: state.cards.map((c) => {
          if (c.cardId !== event.cardId) return c;
          // Filter out memberAvatars from WS events — they contain raw S3 keys,
          // not presigned URLs. The board refresh will resolve them properly.
          const { memberAvatars: _wsAvatars, ...safeFields } = event.fields;
          const merged = { ...c, ...safeFields };
          // Deep-merge customFields: the event may carry only the changed fields,
          // so we merge into the existing map rather than replacing it entirely.
          if (event.fields.customFields && c.customFields) {
            merged.customFields = { ...c.customFields, ...event.fields.customFields };
          }
          return merged;
        }),
      };

    case 'ListCreated':
      if (state.lists.some((l) => l.listId === event.list.listId)) {
        return state;
      }
      return { ...state, lists: [...state.lists, event.list] };

    case 'ListUpdated':
      return {
        ...state,
        lists: state.lists.map((l) =>
          l.listId === event.listId ? { ...l, ...event.fields } : l,
        ),
      };

    case 'SwimlaneCreated': {
      // Ignore if we already know this swimlane (e.g. our own optimistic refetch).
      if (state.swimlanes.some((s) => s.swimlaneId === event.swimlane.swimlaneId)) {
        return state;
      }
      const s = event.swimlane;
      const newSwimlane: SwimlaneSummary = {
        swimlaneId: s.swimlaneId,
        title: s.title ?? '',
        sort: s.sort ?? 0,
        archived: s.archived ?? false,
        color: s.color ?? '',
        cardCount: s.cardCount ?? 0,
        startDate: s.startDate ?? null,
        endDate: s.endDate ?? null,
      };
      return { ...state, swimlanes: [...state.swimlanes, newSwimlane] };
    }

    case 'SwimlaneUpdated':
      // Merge only the changed fields into the matching swimlane. The event may
      // carry a partial `fields` object (e.g. a rename sends only `title`).
      return {
        ...state,
        swimlanes: state.swimlanes.map((s) =>
          s.swimlaneId === event.swimlaneId ? { ...s, ...event.fields } : s,
        ),
      };

    case 'MemberAdded':
      if (!state.board) return state;
      if (state.board.members.some((m) => m.userId === event.member.userId)) {
        return state;
      }
      return {
        ...state,
        board: { ...state.board, members: [...state.board.members, event.member] },
      };

    case 'CustomfieldCreated':
      if (state.customFieldDefinitions.some((d) => d.fieldId === event.field.fieldId)) {
        return state;
      }
      return {
        ...state,
        customFieldDefinitions: [...state.customFieldDefinitions, event.field],
      };

    case 'CustomfieldUpdated':
      return {
        ...state,
        customFieldDefinitions: state.customFieldDefinitions.map((d) =>
          d.fieldId === event.field.fieldId ? event.field : d,
        ),
      };

    case 'CustomfieldDeleted':
      return {
        ...state,
        customFieldDefinitions: state.customFieldDefinitions.filter((d) => d.fieldId !== event.fieldId),
      };

    default:
      return state;
  }
}

// ---------------------------------------------------------------------------
// Context shape
// ---------------------------------------------------------------------------

interface ActiveBoardContextValue {
  readonly state: ActiveBoardState;
  fetchBoard: (boardId: string) => Promise<void>;
  createCard: (boardId: string, input: CreateCardInput) => Promise<void>;
  moveCard: (boardId: string, input: MoveCardInput) => Promise<void>;
  updateCard: (boardId: string, input: UpdateCardInput) => Promise<void>;
  archiveCard: (boardId: string, cardId: string) => Promise<void>;
  restoreCard: (boardId: string, cardId: string, listId: string, swimlaneId: string, title?: string) => Promise<void>;
  reorderList: (boardId: string, listId: string, newSort: number) => Promise<void>;
  archiveList: (boardId: string, listId: string, moveCardsToListId?: string) => Promise<void>;
  dispatch: React.Dispatch<ActiveBoardAction>;
}

const ActiveBoardContext = createContext<ActiveBoardContextValue | null>(null);

// ---------------------------------------------------------------------------
// API response types
// ---------------------------------------------------------------------------

interface BoardAggregateResponse {
  readonly board: BoardDetail;
  readonly lists: ListSummary[];
  readonly swimlanes: SwimlaneSummary[];
  readonly cards: CardSummary[];
  readonly customFields?: CustomFieldDefinition[];
  readonly accessLevel?: string;
  readonly isMember?: boolean;
}

interface CreateCardResponse {
  readonly commandId: string;
  readonly status: string;
  readonly correlationId: string;
  readonly cardId?: string;
  readonly cardNumber?: number;
}

// ---------------------------------------------------------------------------
// Provider component
// ---------------------------------------------------------------------------

interface ActiveBoardProviderProps {
  readonly children: React.ReactNode;
  /** Board ID to fetch on mount. If undefined, no fetch occurs. */
  readonly boardId?: string;
  /** Skip initial fetch — useful for testing */
  readonly skipInitialFetch?: boolean;
}

export function ActiveBoardProvider({
  children,
  boardId,
  skipInitialFetch,
}: ActiveBoardProviderProps): React.ReactElement {
  const [state, dispatch] = useReducer(activeBoardReducer, initialActiveBoardState);
  const stateRef = useRef<ActiveBoardState>(state);
  stateRef.current = state;

  const fetchBoard = useCallback(async (id: string): Promise<void> => {
    dispatch({ type: 'FETCH_BOARD_START' });
    try {
      const response = await api.get<BoardAggregateResponse>(`/v1/boards/${id}`);
      dispatch({
        type: 'FETCH_BOARD_SUCCESS',
        payload: {
          board: response.board,
          lists: response.lists,
          swimlanes: response.swimlanes,
          cards: response.cards,
          customFields: response.customFields,
          accessLevel: response.accessLevel,
          isMember: response.isMember,
        },
      });
    } catch (error: unknown) {
      const message = extractErrorMessage(error);
      dispatch({ type: 'FETCH_BOARD_ERROR', payload: message });
    }
  }, []);

  const createCard = useCallback(async (id: string, input: CreateCardInput): Promise<void> => {
    const previousState = stateRef.current;
    const tempId = `temp-${crypto.randomUUID()}`;
    const optimisticCard: CardSummary = {
      cardId: tempId,
      cardNumber: 0,
      title: input.title,
      listId: input.listId,
      swimlaneId: input.swimlaneId,
      sort: 999999,
      labels: [],
      members: [],
      memberAvatars: [],
      dueDate: null,
      startDate: null,
      coverImage: null,
      checklistProgress: null,
      subtaskProgress: null,
      isSubtask: false,
      parentCardId: null,
      customFields: {},
    };

    dispatch({ type: 'CREATE_CARD_OPTIMISTIC', payload: optimisticCard });

    try {
      const response = await api.post<CreateCardResponse>(`/v1/boards/${id}/cards`, input);
      dispatch({
        type: 'CREATE_CARD_CONFIRMED',
        payload: {
          tempId,
          cardId: response.cardId ?? tempId,
          cardNumber: response.cardNumber ?? 0,
        },
      });
    } catch {
      dispatch({ type: 'CREATE_CARD_REVERT', payload: { cardId: tempId, previousState } });
    }
  }, []);

  const moveCard = useCallback(async (id: string, input: MoveCardInput): Promise<void> => {
    const previousState = stateRef.current;

    dispatch({ type: 'MOVE_CARD_OPTIMISTIC', payload: input });

    try {
      const effectiveSort = input.sort !== undefined && input.sort >= 0 ? input.sort : undefined;
      const response = await api.put<{ data: { sort?: number } }>(`/v1/boards/${id}/cards/${input.cardId}`, {
        listId: input.targetListId,
        swimlaneId: input.targetSwimlaneId,
        ...(effectiveSort !== undefined ? { sort: effectiveSort } : {}),
      });
      // Update card with the real sort value from the backend
      const realSort = response?.data?.sort;
      if (realSort !== undefined) {
        dispatch({
          type: 'MOVE_CARD_OPTIMISTIC',
          payload: { ...input, sort: realSort },
        });
      }
      dispatch({ type: 'MOVE_CARD_CONFIRMED' });
    } catch {
      dispatch({ type: 'MOVE_CARD_REVERT', payload: { previousState } });
    }
  }, []);

  const updateCard = useCallback(async (id: string, input: UpdateCardInput): Promise<void> => {
    const previousState = stateRef.current;

    dispatch({ type: 'UPDATE_CARD_OPTIMISTIC', payload: input });

    try {
      // Strip frontend-only fields that the API doesn't accept
      const { memberAvatars: _avatars, ...apiPayload } = input;
      await api.patch(`/v1/boards/${id}/cards/${input.cardId}`, apiPayload);
      dispatch({ type: 'UPDATE_CARD_CONFIRMED' });
    } catch {
      dispatch({ type: 'UPDATE_CARD_REVERT', payload: { previousState } });
    }
  }, []);

  const archiveCard = useCallback(async (boardId: string, cardId: string): Promise<void> => {
    const previousState = stateRef.current;

    dispatch({ type: 'ARCHIVE_CARD_OPTIMISTIC', payload: { cardId } });

    try {
      await api.put(`/v1/boards/${boardId}/cards/${cardId}`, { archived: true });
      dispatch({ type: 'ARCHIVE_CARD_CONFIRMED' });
    } catch {
      dispatch({ type: 'ARCHIVE_CARD_REVERT', payload: { previousState } });
    }
  }, []);

  const restoreCard = useCallback(async (boardId: string, cardId: string, listId: string, swimlaneId: string, title?: string): Promise<void> => {
    const previousState = stateRef.current;
    const card = previousState.cards.find((c) => c.cardId === cardId);

    const restoredCard: CardSummary = card
      ? { ...card, listId, swimlaneId }
      : {
          cardId,
          cardNumber: 0,
          title: title ?? '',
          listId,
          swimlaneId,
          sort: 999999,
          labels: [],
          members: [],
          memberAvatars: [],
          dueDate: null,
          startDate: null,
          coverImage: null,
          checklistProgress: null,
          subtaskProgress: null,
          isSubtask: false,
          parentCardId: null,
          customFields: {},
        };

    dispatch({ type: 'RESTORE_CARD_OPTIMISTIC', payload: { card: restoredCard } });

    try {
      await api.put(`/v1/boards/${boardId}/cards/${cardId}`, { archived: false, listId, swimlaneId });
      dispatch({ type: 'RESTORE_CARD_CONFIRMED' });
    } catch {
      dispatch({ type: 'RESTORE_CARD_REVERT', payload: { previousState } });
    }
  }, []);

  const reorderList = useCallback(async (boardId: string, listId: string, newSort: number): Promise<void> => {
    const previousState = stateRef.current;

    dispatch({ type: 'REORDER_LIST_OPTIMISTIC', payload: { listId, newSort } });

    try {
      await api.put(`/v1/boards/${boardId}/lists/reorder`, { listId, newSort });
      dispatch({ type: 'REORDER_LIST_CONFIRMED' });
    } catch {
      dispatch({ type: 'REORDER_LIST_REVERT', payload: { previousState } });
    }
  }, []);

  const archiveList = useCallback(async (boardId: string, listId: string, moveCardsToListId?: string): Promise<void> => {
    const previousState = stateRef.current;

    // Optimistically mark list as archived and remove its cards from view
    dispatch({ type: 'ARCHIVE_LIST_OPTIMISTIC', payload: { listId } });

    try {
      await api.put(`/v1/boards/${boardId}/lists/${listId}`, {
        archived: true,
        ...(moveCardsToListId ? { moveCardsTo: moveCardsToListId } : {}),
      });
      dispatch({ type: 'ARCHIVE_LIST_CONFIRMED' });
      // Refetch board to get updated card positions if cards were moved
      if (moveCardsToListId) {
        await fetchBoard(boardId);
      }
    } catch {
      dispatch({ type: 'ARCHIVE_LIST_REVERT', payload: { previousState } });
    }
  }, [fetchBoard]);

  // Fetch board on mount when boardId is provided
  useEffect(() => {
    if (boardId && !skipInitialFetch) {
      fetchBoard(boardId);
    }
  }, [boardId, fetchBoard, skipInitialFetch]);

  const value: ActiveBoardContextValue = {
    state,
    fetchBoard,
    createCard,
    moveCard,
    updateCard,
    archiveCard,
    restoreCard,
    reorderList,
    archiveList,
    dispatch,
  };

  return <ActiveBoardContext.Provider value={value}>{children}</ActiveBoardContext.Provider>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Access the active board context. Must be used within an ActiveBoardProvider.
 */
export function useActiveBoard(): ActiveBoardContextValue {
  const context = useContext(ActiveBoardContext);
  if (!context) {
    throw new Error('useActiveBoard must be used within an ActiveBoardProvider');
  }
  return context;
}

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

function extractErrorMessage(error: unknown): string {
  if (api.isClientError(error)) {
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unexpected error occurred';
}

function stripUndefined(obj: Partial<Record<string, unknown>>): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined) {
      result[key] = value;
    }
  }
  return result;
}

// Export reducer for testing
export { activeBoardReducer as reducer };
